/**
 * OracleReadingPage
 * Açılım sonuç sayfası - Loading + Netflix-style results
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OracleShell from '../components/OracleShell';
import CinematicProgress from '../components/CinematicProgress';
import ReadingStep from '../components/ReadingStep';
import EnergyScoreCard from '../components/EnergyScoreCard';
import ShareCard from '../components/ShareCard';
import FollowupSuggestions from '../components/FollowupSuggestions';
import * as oracleApi from '../services/oracleApi';

export default function OracleReadingPage({ token, credits, onCreditsUpdate }) {
  const { readingId } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('loading');
  const [progress, setProgress] = useState({ percent: 0, message: 'Yükleniyor...', icon: '⏳' });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [followupRemaining, setFollowupRemaining] = useState(2);
  
  // Progress stages
  const STAGES = {
    'pending': { icon: '⏳', message: 'Açılım hazırlanıyor...' },
    'analyzing_image': { icon: '☕', message: 'Fincandaki semboller okunuyor...' },
    'drawing_cards': { icon: '🃏', message: 'Kartlar çekiliyor...' },
    'generating_story': { icon: '✨', message: 'Kader yolları birleştiriliyor...' },
    'formatting': { icon: '📜', message: 'Açılım yazılıyor...' },
    'completed': { icon: '✅', message: 'Açılım tamamlandı!' }
  };
  
  // Poll status
  const pollStatus = useCallback(async () => {
    try {
      const statusData = await oracleApi.getReadingStatus(token, readingId);
      const stage = STAGES[statusData.status] || STAGES.pending;
      
      setStatus(statusData.status);
      setProgress({
        percent: statusData.progress_percent || 0,
        message: statusData.progress_message || stage.message,
        icon: stage.icon
      });
      
      if (statusData.status === 'completed') {
        const resultData = await oracleApi.getReadingResult(token, readingId);
        setResult(resultData);
        setFollowupRemaining(resultData.followup_remaining || 2);
        if (onCreditsUpdate) onCreditsUpdate();
        return true; // Stop polling
      } else if (statusData.status === 'failed') {
        setError(statusData.error || 'Açılım başarısız oldu');
        return true; // Stop polling
      }
      
      return false; // Continue polling
    } catch (err) {
      console.error('Poll error:', err);
      return false;
    }
  }, [token, readingId, onCreditsUpdate]);
  
  // Initial load and polling
  useEffect(() => {
    let intervalId = null;
    let mounted = true;
    
    const startPolling = async () => {
      // İlk kontrol
      const done = await pollStatus();
      
      if (!done && mounted) {
        intervalId = setInterval(async () => {
          const finished = await pollStatus();
          if (finished && intervalId) {
            clearInterval(intervalId);
          }
        }, 2000);
      }
    };
    
    startPolling();
    
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [pollStatus]);
  
  // Sections for Netflix-style navigation
  const sections = result?.story_sections || [];
  const totalSteps = sections.length + 1; // +1 for final card
  const isLastStep = currentStep >= sections.length;
  
  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Followup handler
  const handleFollowup = async (question) => {
    try {
      const response = await oracleApi.sendFollowup(token, readingId, question);
      setFollowupRemaining(response.remaining);
      // Show answer somehow - could be a modal or new section
      alert(`${response.answer}`);
    } catch (err) {
      setError(err.message);
    }
  };
  
  const handleBack = () => {
    navigate('/oracle');
  };
  
  const handleNewReading = () => {
    navigate('/oracle/new');
  };
  
  // Loading state
  if (status !== 'completed' && status !== 'failed') {
    return (
      <OracleShell credits={credits}>
        <CinematicProgress
          percent={progress.percent}
          message={progress.message}
          icon={progress.icon}
        />
      </OracleShell>
    );
  }
  
  // Error state
  if (error) {
    return (
      <OracleShell showBack onBack={handleBack} credits={credits}>
        <div className="oracle-content" style={{ textAlign: 'center', paddingTop: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>😔</div>
          <h2 style={{ marginBottom: 8 }}>Bir Hata Oluştu</h2>
          <p style={{ color: 'var(--oracle-text-muted)', marginBottom: 24 }}>{error}</p>
          <button className="oracle-btn-primary" onClick={handleNewReading}>
            Tekrar Dene
          </button>
        </div>
      </OracleShell>
    );
  }
  
  // Result state
  return (
    <OracleShell showBack onBack={handleBack} credits={credits}>
      
      {/* Header with oracle name */}
      <div className="oracle-result-header">
        {result?.oracle_metadata?.oracle_name && (
          <div className="oracle-result-name">
            ✨ {result.oracle_metadata.oracle_name}
          </div>
        )}
        <h1 className="oracle-result-title">Açılımın Hazır</h1>
      </div>
      
      {/* Step progress indicator */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: 4, 
        padding: '16px 20px',
        borderBottom: '1px solid var(--oracle-border)'
      }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentStep(i)}
            style={{
              width: currentStep === i ? 24 : 8,
              height: 8,
              borderRadius: 4,
              border: 'none',
              background: i <= currentStep ? 'var(--oracle-purple-400)' : 'var(--oracle-border)',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>
      
      {/* Content sections */}
      {!isLastStep ? (
        <>
          <ReadingStep 
            section={sections[currentStep]} 
            isActive={true}
          />
          
          {/* Navigation */}
          <div className="oracle-step-nav">
            <button 
              className="oracle-btn-secondary"
              onClick={prevStep}
              disabled={currentStep === 0}
              style={{ opacity: currentStep === 0 ? 0.5 : 1 }}
            >
              Önceki
            </button>
            <button 
              className="oracle-btn-primary"
              onClick={nextStep}
              data-testid="next-section-btn"
            >
              {currentStep === sections.length - 1 ? 'Final' : 'Devam'}
            </button>
          </div>
        </>
      ) : (
        /* Final Card */
        <>
          <EnergyScoreCard scores={result?.energy_scores} />
          
          <ShareCard
            oracleName={result?.oracle_metadata?.oracle_name}
            hiddenMessage={result?.oracle_metadata?.hidden_message}
            signatureSentence={result?.oracle_metadata?.signature_sentences}
          />
          
          <FollowupSuggestions
            suggestions={result?.followup_suggestions}
            onSelect={handleFollowup}
            remaining={followupRemaining}
          />
          
          {/* Actions */}
          <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button 
              className="oracle-btn-secondary"
              onClick={() => setCurrentStep(0)}
              style={{ width: '100%' }}
            >
              Baştan Oku
            </button>
            <button 
              className="oracle-btn-primary"
              onClick={handleNewReading}
              style={{ width: '100%' }}
              data-testid="new-reading-btn"
            >
              Yeni Açılım
            </button>
          </div>
        </>
      )}
      
    </OracleShell>
  );
}
